import {
  Button,
  Card,
  CardHeader,
  FormField,
  InlineAlert,
  PageHeader,
  Spinner,
  TextInput,
  Select,
} from "@constructorfleet/ui/design-system";
import { normalizeUsPhoneNumber, useMount } from "@constructorfleet/ui";
import {
  ACCOUNT_CREATION_DISABLED_NOTICE,
  isAccountCreationDisabled,
  startPaymentCheckout,
} from "@constructorfleet/offerings-sdk";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  formatBillingLabel,
  formatCurrencyAmount,
  getBhgCallCheckoutPlanName,
  getBhgCallCheckoutPricing,
} from "@constructorfleet/bhg-product-core";
import { bhgCallCatalog } from "@constructorfleet/bhg-product-core";
import { liveBhgCallDashboardProvider } from "../bhg-call-dashboard-api";
import { BhgClinicSettings } from "../components/BhgClinicSettings";
import {
  BhgNotificationSettings,
  filterNotificationChannelsByAvailability,
  type BhgNotificationSettingsMessages,
} from "../components/BhgNotificationSettings";
import {
  BhgNotificationScheduleSettings,
  type BhgNotificationScheduleSettingsMessages,
} from "../components/BhgNotificationScheduleSettings";
import {
  DEFAULT_BHG_NOTIFICATION_TIME,
  convertBhgNotificationTimeToUtc,
  getBhgNotificationTimeError,
  isValidBhgNotificationTime,
  isValidBhgNotificationTimezone,
  normalizeBhgNotificationTimezone,
} from "@constructorfleet/bhg-product-core";
import type { BhgCallDashboardProvider, BhgClinicOption, NotificationChannel } from "../types";
import { BHG_OFFERING_ID } from "@constructorfleet/bhg-product-core";
import { BHG_ROUTES } from "@constructorfleet/bhg-product-core";

const BILLING_PROVIDER_META_NAME = "billing-provider";
const BILLING_PROVIDER_CHANGED_EVENT = "constructorfleet:billing-provider-changed";
const bhgCallPricing = getBhgCallCheckoutPricing();
const bhgCallPlanName = getBhgCallCheckoutPlanName();
const bhgCallPriceLabel = formatBillingLabel(bhgCallPricing.price, bhgCallPricing.interval);
const bhgCallTotalDueToday = formatCurrencyAmount(bhgCallPricing.price);
const bhgCallSummary = bhgCallCatalog.marketing.summary;
const bhgCallDescription = bhgCallCatalog.marketing.description;

// ---------------------------------------------------------------------------
// Local storage helpers
// ---------------------------------------------------------------------------

type StoredAuth = {
  username?: string;
  email?: string;
  role?: "user" | "admin";
  phoneNumber?: string;
};

type SignupFormError = {
  last4Ssn?: string;
  last4PatientId?: string;
  clinic?: string;
  notificationTime?: string;
  notificationTimezone?: string;
} & BhgNotificationSettingsMessages;

type ServiceApplicationRecord = {
  id: string;
  status: "pending" | "approved" | "rejected" | (string & {});
  requestedType: "discount" | "comp" | (string & {});
  requestedDiscountPercent: number | null;
  approvedDiscountPercent: number | null;
  reason: string;
  decisionNotes: string;
  createdAt: string;
  supportingDocumentS3Key: string | null;
};

function readJson<T>(key: string) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const sessionToken = localStorage.getItem("cf-session-token");
  return sessionToken ? { "x-session-token": sessionToken } : {};
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchClinicsForSignup(): Promise<BhgClinicOption[]> {
  const sessionToken = localStorage.getItem("cf-session-token");
  const headers: Record<string, string> = { Accept: "application/json" };
  if (sessionToken) headers["x-session-token"] = sessionToken;
  const response = await fetch("/api/website/bhg-clinics", { headers });
  if (!response.ok) return [];
  const data = (await response.json()) as { clinics?: BhgClinicOption[] };
  return data.clinics ?? [];
}

async function persistEnrollment(input: {
  ssnLast4: string;
  patientIdLast4: string;
  notificationTime: string;
  notificationTimezone: string;
  mobilePhone: string;
  notificationEmail: string;
  notificationChannels: ("sms" | "email")[];
  clinicId: number | null;
}) {
  const sessionToken = localStorage.getItem("cf-session-token");
  if (!sessionToken) return;
  const response = await fetch("/api/website/bhg-enrollment", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": sessionToken,
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error("Unable to save BHG enrollment details.");
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateBhgFields(input: {
  ssnLast4: string;
  patientIdLast4: string;
  selectedClinicId: string;
  selectedClinicDosingHours: string | null;
  selectedTime: string;
  selectedTimezone: string;
  selectedChannels: NotificationChannel[];
  enabledChannels: NotificationChannel[];
  selectedPhone: string;
  selectedEmail: string;
  consentToMessages: boolean;
}): SignupFormError {
  const messages: SignupFormError = {};
  if (!/^\d{4}$/.test(input.ssnLast4)) {
    messages.last4Ssn = "Last 4 of social must be exactly 4 digits.";
  }
  if (!/^\d{4}$/.test(input.patientIdLast4)) {
    messages.last4PatientId = "Last 4 of patient ID must be exactly 4 digits.";
  }
  if (!input.selectedClinicId) {
    messages.clinic = "Please select your clinic location.";
  }
  if (!isValidBhgNotificationTime(input.selectedTime)) {
    messages.notificationTime = "Notification time must be a valid time.";
  }
  const dosingWindowError = getBhgNotificationTimeError(
    input.selectedTime,
    input.selectedClinicDosingHours
  );
  if (dosingWindowError) {
    messages.notificationTime = dosingWindowError;
  }
  if (!isValidBhgNotificationTimezone(input.selectedTimezone)) {
    messages.notificationTimezone = "Please select one of the supported US timezones.";
  }
  if (input.selectedChannels.length === 0) {
    messages.notificationChannels = "Select at least one notification channel.";
  }
  if (input.enabledChannels.length === 0) {
    messages.notificationChannels =
      "Notification channels are currently disabled by your administrator.";
  } else {
    const enabledSet = new Set(input.enabledChannels);
    const selectedDisabledChannel = input.selectedChannels.some(
      (channel) => !enabledSet.has(channel)
    );
    if (selectedDisabledChannel) {
      messages.notificationChannels = "Selected notification channels are not currently enabled.";
    }
  }

  const hasPhone = normalizeUsPhoneNumber(input.selectedPhone).length === 10;
  const hasEmail = input.selectedEmail.trim().length > 0;

  if (input.selectedChannels.includes("sms") && !hasPhone) {
    messages.phoneNumber = "A valid phone number is required for SMS notifications.";
  }
  if (input.selectedChannels.includes("email") && !hasEmail) {
    messages.email = "A notification email is required for email notifications.";
  }
  if (hasPhone && !input.consentToMessages) {
    messages.consentToMessages = "You must consent to receive BHG notifications before continuing.";
  }
  if (!hasPhone && !hasEmail) {
    messages.provider =
      "Provide at least one notification contact — a phone number or email address.";
  }
  return messages;
}

// ---------------------------------------------------------------------------
// Billing provider detection
// ---------------------------------------------------------------------------

function readBillingProviderMeta(): string {
  if (typeof document === "undefined") return "demo";
  const meta = document.querySelector<HTMLMetaElement>(
    `meta[name="${BILLING_PROVIDER_META_NAME}"]`
  );
  return meta?.content ?? "demo";
}

async function loadBillingProviderFromApi(): Promise<string | null> {
  try {
    const response = await fetch("/api/website/billing-provider");
    if (!response.ok) return null;
    const payload = (await response.json()) as { ok?: boolean; billingProvider?: string };
    return payload.billingProvider ?? null;
  } catch {
    return null;
  }
}

export function useBillingProvider(): string {
  const [billingProvider, setBillingProvider] = useState(() => readBillingProviderMeta());

  useEffect(() => {
    const handleProviderChange = (event: Event) => {
      const nextProvider = (event as CustomEvent<string>).detail ?? readBillingProviderMeta();
      setBillingProvider(nextProvider);
    };

    window.addEventListener(BILLING_PROVIDER_CHANGED_EVENT, handleProviderChange);
    return () => {
      window.removeEventListener(BILLING_PROVIDER_CHANGED_EVENT, handleProviderChange);
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const nextProvider = await loadBillingProviderFromApi();
      if (!active || !nextProvider) return;
      setBillingProvider(nextProvider);
    })();

    return () => {
      active = false;
    };
  }, []);

  return billingProvider;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BhgCallSignupPage() {
  const navigate = useNavigate();
  const accountCreationDisabled = isAccountCreationDisabled();
  const auth = readJson<StoredAuth>("constructorfleet-auth");
  const isAuthenticatedUser = auth?.role === "user" && Boolean(auth.username);
  const billingProvider = useBillingProvider();
  const isDemo = billingProvider === "demo" || billingProvider === "memberful";

  // BHG identity fields
  const [ssnLast4, setSsnLast4] = useState("");
  const [patientIdLast4, setPatientIdLast4] = useState("");

  // BhgClinicSettings state
  const [clinics, setClinics] = useState<BhgClinicOption[]>([]);
  const [currentClinicId, setCurrentClinicId] = useState("");
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [clinicName, setClinicName] = useState<string | null>(null);

  // BhgNotificationScheduleSettings state
  const [currentTime, setCurrentTime] = useState(DEFAULT_BHG_NOTIFICATION_TIME);
  const [selectedTime, setSelectedTime] = useState(DEFAULT_BHG_NOTIFICATION_TIME);
  const [currentTimezone, setCurrentTimezone] = useState(() =>
    normalizeBhgNotificationTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  );
  const [selectedTimezone, setSelectedTimezone] = useState(() =>
    normalizeBhgNotificationTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  );

  // BhgNotificationSettings state
  const [currentPhone, setCurrentPhone] = useState("");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [currentChannels, setCurrentChannels] = useState<NotificationChannel[]>(["email"]);
  const [selectedChannels, setSelectedChannels] = useState<NotificationChannel[]>(["email"]);
  const [enabledNotificationChannels, setEnabledNotificationChannels] = useState<
    NotificationChannel[]
  >(["sms", "email"]);
  const [currentConsent, setCurrentConsent] = useState(false);
  const [consentToMessages, setConsentToMessages] = useState(false);

  // Checkout step
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutProcessing, setCheckoutProcessing] = useState(false);
  const [cardName, setCardName] = useState("");
  const [cardNumber] = useState("4242 4242 4242 4242");
  const [cardExpiry] = useState("12/28");
  const [cardCvc] = useState("123");
  const [assistanceApplications, setAssistanceApplications] = useState<ServiceApplicationRecord[]>(
    []
  );
  const [assistanceRequestedType, setAssistanceRequestedType] = useState<"discount" | "comp">(
    "discount"
  );
  const [assistanceDiscountPercent, setAssistanceDiscountPercent] = useState("25");
  const [assistanceReason, setAssistanceReason] = useState("");
  const [assistanceSubmitting, setAssistanceSubmitting] = useState(false);
  const [assistanceMessage, setAssistanceMessage] = useState<string | null>(null);
  const [assistanceFile, setAssistanceFile] = useState<File | null>(null);

  // Shared across all settings components
  const [saving, setSaving] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState<BhgNotificationScheduleSettingsMessages>(
    {}
  );
  const [notifMessage, setNotifMessage] = useState<BhgNotificationSettingsMessages>({});
  const [formError, setFormError] = useState<SignupFormError>({});
  const selectedClinic = clinics.find((clinic) => String(clinic.id) === selectedClinicId) ?? null;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/website/bhg-assistance-applications", {
          headers: buildAuthHeaders(),
        });
        if (!response.ok) return;
        const payload = (await response.json()) as {
          ok?: boolean;
          applications?: ServiceApplicationRecord[];
        };
        if (!cancelled && payload.ok !== false) {
          setAssistanceApplications(payload.applications ?? []);
        }
      } catch {
        // Ignore failures; the form is still usable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth?.role === "admin") return <Navigate to="/admin" replace />;
  if (accountCreationDisabled) {
    return (
      <div className="page ds-stack">
        <PageHeader title={`${bhgCallPlanName} • ${bhgCallPriceLabel}`} sub={bhgCallSummary} />
        <InlineAlert tone="info">{ACCOUNT_CREATION_DISABLED_NOTICE}</InlineAlert>
        <Card>
          <div className="card-body ds-stack">
            <h3>Enrollment paused</h3>
            <p>
              Existing users can still sign in on the <Link to="/login">login page</Link>, but new
              account creation and BHG enrollment are temporarily disabled.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const bhgIdentityCard = (
    <Card>
      <div className="card-body ds-stack">
        <h3>BHG Identity</h3>
        <p>Used to identify you in the BHG IVR system.</p>
        <FormField label="Last 4 of Social" htmlFor="signup-ssn-last4" required>
          <TextInput
            id="signup-ssn-last4"
            inputMode="numeric"
            maxLength={4}
            value={ssnLast4}
            onChange={(event) => setSsnLast4(event.target.value.replace(/\D/g, "").slice(0, 4))}
            required
          />
        </FormField>
        <FormField label="Last 4 of Patient ID" htmlFor="signup-patient-id-last4" required>
          <TextInput
            id="signup-patient-id-last4"
            inputMode="numeric"
            maxLength={4}
            value={patientIdLast4}
            onChange={(event) =>
              setPatientIdLast4(event.target.value.replace(/\D/g, "").slice(0, 4))
            }
            required
          />
        </FormField>
      </div>
    </Card>
  );

  const clinicSettingsProps = {
    context: "signup" as const,
    clinics,
    setClinics,
    currentClinicId,
    setCurrentClinicId,
    selectedClinicId,
    setSelectedClinicId,
    clinicName,
    setClinicName,
    saving,
    setSaving,
    message: formError.clinic || null,
    setMessage: (message: string | null) => {
      setFormError((prev) => ({ ...prev, clinic: message ? message : undefined }));
    },
  };

  const scheduleSettingsProps = {
    dosingHoursJson: selectedClinic?.dosingHours ?? null,
    currentTime,
    setCurrentTime,
    selectedTime,
    setSelectedTime,
    currentTimezone,
    setCurrentTimezone,
    selectedTimezone,
    setSelectedTimezone,
    saving,
    setSaving,
    message: scheduleMessage,
    setMessage: setScheduleMessage,
  };

  const notifSettingsProps = {
    accountPhone: auth?.phoneNumber ? normalizeUsPhoneNumber(auth.phoneNumber) : undefined,
    accountEmail: auth?.email || undefined,
    currentPhone,
    setCurrentPhone,
    selectedPhone,
    setSelectedPhone,
    currentEmail,
    setCurrentEmail,
    selectedEmail,
    setSelectedEmail,
    currentChannels,
    setCurrentChannels,
    selectedChannels,
    setSelectedChannels,
    availableChannels: enabledNotificationChannels,
    currentConsent,
    setCurrentConsent,
    consentToMessages,
    setConsentToMessages,
    saving,
    setSaving,
    message: notifMessage,
    setMessage: setNotifMessage,
  };

  const hasError = Object.values(formError).some((error) => error);
  const formErrorMessages = Object.values(formError).filter((error) => error) as string[];

  useMemo(() => {
    setFormError((prev) => ({
      ...prev,
      ...notifMessage,
    }));
  }, [notifMessage]);

  useMount(async () => (accountCreationDisabled ? [] : await fetchClinicsForSignup())).then(
    setClinics
  );
  useMount(async () => {
    if (accountCreationDisabled) {
      return null;
    }
    const enabledChannels = (
      await liveBhgCallDashboardProvider.fetchNotificationChannelAvailability?.()
    )?.enabledChannels ?? ["sms", "email"];
    return enabledChannels;
  }).then((enabledChannels) => {
    if (!enabledChannels) {
      return;
    }
    setEnabledNotificationChannels(enabledChannels);
    setCurrentChannels((previous) => {
      const filtered = filterNotificationChannelsByAvailability(previous, enabledChannels);
      return filtered.length > 0 ? filtered : enabledChannels.slice(0, 1);
    });
    setSelectedChannels((previous) => {
      const filtered = filterNotificationChannelsByAvailability(previous, enabledChannels);
      return filtered.length > 0 ? filtered : enabledChannels.slice(0, 1);
    });
  });

  const handleBhgEnrollment = async (userKey: string): Promise<boolean> => {
    const validationError = validateBhgFields({
      ssnLast4,
      patientIdLast4,
      selectedClinicId,
      selectedClinicDosingHours: selectedClinic?.dosingHours ?? null,
      selectedTime,
      selectedTimezone,
      selectedChannels,
      enabledChannels: enabledNotificationChannels,
      selectedPhone,
      selectedEmail,
      consentToMessages,
    });
    if (Object.keys(validationError).length > 0) {
      setFormError(validationError);
      return false;
    }

    const allowedChannels = filterNotificationChannelsByAvailability(
      selectedChannels,
      enabledNotificationChannels
    );
    const notificationTimeUtc = convertBhgNotificationTimeToUtc(selectedTime, selectedTimezone);
    await persistEnrollment({
      ssnLast4,
      patientIdLast4,
      notificationTime: notificationTimeUtc,
      notificationTimezone: selectedTimezone,
      mobilePhone: normalizeUsPhoneNumber(selectedPhone),
      notificationEmail: selectedEmail.trim(),
      notificationChannels: allowedChannels,
      clinicId: selectedClinicId ? Number(selectedClinicId) : null,
    });

    return true;
  };

  // ---- Unauthenticated: redirect to login ----
  if (!isAuthenticatedUser) {
    return <Navigate to="/login" state={{ from: `/${BHG_ROUTES.SIGNUP}` }} replace />;
  }

  // ---- Authenticated without BHG: BHG enrollment only ----
  const handleProceedToCheckout = (event: { preventDefault(): void }) => {
    event.preventDefault();
    setFormError({});

    const validationError = validateBhgFields({
      ssnLast4,
      patientIdLast4,
      selectedClinicId,
      selectedClinicDosingHours: selectedClinic?.dosingHours ?? null,
      selectedTime,
      selectedTimezone,
      selectedChannels,
      enabledChannels: enabledNotificationChannels,
      selectedPhone,
      selectedEmail,
      consentToMessages,
    });
    if (Object.keys(validationError).length > 0) {
      setFormError(validationError);
      return;
    }

    setCardName(auth?.username ?? "");
    setShowCheckout(true);
  };

  const handleCheckoutSubmit = async (event: { preventDefault(): void }) => {
    event.preventDefault();
    setCheckoutProcessing(true);
    setFormError({});

    if (isDemo) {
      // Simulate payment processing delay for demo mode
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setSaving(true);
      setCheckoutProcessing(false);
      const ok = await handleBhgEnrollment(auth?.username ?? "");
      setSaving(false);
      if (ok) navigate(`/${BHG_ROUTES.DASHBOARD}`);
    } else {
      // Real provider: enroll, then redirect to provider checkout
      setSaving(true);
      const ok = await handleBhgEnrollment(auth?.username ?? "");
      setSaving(false);
      if (ok) {
        try {
          const checkout = await startPaymentCheckout({
            planId: BHG_OFFERING_ID,
            planName: bhgCallPlanName,
            pricing: {
              price: bhgCallPricing.price,
              interval: bhgCallPricing.interval,
            },
            successUrl: `${window.location.origin}/${BHG_ROUTES.DASHBOARD}`,
            cancelUrl: window.location.href,
          });
          window.location.href = checkout.checkoutUrl;
        } catch (err) {
          setCheckoutProcessing(false);
          setFormError({
            provider: err instanceof Error ? err.message : "Unable to start checkout.",
          });
        }
      } else {
        setCheckoutProcessing(false);
      }
    }
  };

  const handleAssistanceSubmit = async (event: { preventDefault(): void }) => {
    event.preventDefault();
    setAssistanceSubmitting(true);
    setAssistanceMessage(null);

    try {
      const response = await fetch("/api/website/bhg-assistance-applications", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({
          requestedType: assistanceRequestedType,
          requestedDiscountPercent:
            assistanceRequestedType === "comp" ? 100 : Number(assistanceDiscountPercent),
          reason: assistanceReason,
        }),
      });
      const payload = (await response.json()) as {
        ok?: boolean;
        error?: string;
        application?: ServiceApplicationRecord;
      };
      if (!response.ok || payload.ok === false) {
        setAssistanceMessage(payload.error ?? "Unable to submit application.");
        return;
      }
      if (payload.application) {
        // Upload supporting document if provided
        if (assistanceFile) {
          try {
            const arrayBuffer = await assistanceFile.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(arrayBuffer).reduce(
                (data, byte) => data + String.fromCharCode(byte),
                ""
              )
            );
            const uploadResponse = await fetch(
              "/api/website/bhg-assistance-applications/upload-document",
              {
                method: "POST",
                headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
                body: JSON.stringify({
                  applicationId: payload.application.id,
                  filename: assistanceFile.name,
                  fileBase64: base64,
                }),
              }
            );
            if (!uploadResponse.ok) {
              const uploadPayload = (await uploadResponse.json()) as { error?: string };
              setAssistanceMessage(
                `Application submitted, but document upload failed: ${uploadPayload.error ?? "Unknown error"}`
              );
              setAssistanceApplications((prev) => [payload.application!, ...prev]);
              setAssistanceReason("");
              setAssistanceFile(null);
              return;
            }
          } catch {
            setAssistanceMessage("Application submitted, but document upload failed.");
            setAssistanceApplications((prev) => [payload.application!, ...prev]);
            setAssistanceReason("");
            setAssistanceFile(null);
            return;
          }
        }
        setAssistanceApplications((prev) => [payload.application!, ...prev]);
      }
      setAssistanceReason("");
      setAssistanceFile(null);
      setAssistanceMessage("Application submitted.");
    } catch {
      setAssistanceMessage("Unable to submit application.");
    } finally {
      setAssistanceSubmitting(false);
    }
  };

  if (showCheckout) {
    if (!isDemo) {
      const providerLabels: Record<string, string> = {
        stripe: "Stripe",
        paddle: "Paddle",
        lemonsqueezy: "Lemon Squeezy",
      };
      const providerLabel = providerLabels[billingProvider] ?? "our payment partner";
      return (
        <div className="page ds-stack">
          <PageHeader title="Checkout" sub={`Complete your ${bhgCallPlanName} subscription`} />
          <Card>
            <div className="card-body ds-stack">
              <p>You will be redirected to {providerLabel} to complete your purchase securely.</p>
            </div>
          </Card>
          {hasError && (
            <Card>
              <ul>
                {formErrorMessages.map((error, index) => (
                  <li key={index} className="cf-error">
                    {error}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <div className="ds-row">
            <Button
              type="button"
              disabled={checkoutProcessing || saving}
              onClick={(e) => void handleCheckoutSubmit(e)}
            >
              {checkoutProcessing || saving ? (
                <>
                  <Spinner size="sm" />
                  {saving ? "Activating subscription..." : "Redirecting..."}
                </>
              ) : (
                "Continue to payment"
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCheckout(false)}
              disabled={checkoutProcessing || saving}
            >
              Back
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="page ds-stack">
        <PageHeader
          title="Demo Checkout"
          sub={`Complete your ${bhgCallPlanName} subscription — ${bhgCallPriceLabel}`}
        />

        <InlineAlert tone="info">
          <strong>DEMO MODE</strong> — This is a simulated payment processor. No real payment will
          be collected. Card details are pre-filled placeholders.
        </InlineAlert>

        <form className="ds-stack" onSubmit={(e) => void handleCheckoutSubmit(e)}>
          <Card>
            <div className="card-body ds-stack">
              <h3>Order Summary</h3>
              <div className="ds-between">
                <strong>{bhgCallPlanName}</strong>
                <span>{bhgCallPriceLabel}</span>
              </div>
              <p className="cf-help">{bhgCallDescription}</p>
              <hr />
              <div className="ds-between">
                <strong>Total due today</strong>
                <strong>{bhgCallTotalDueToday}</strong>
              </div>
            </div>
          </Card>

          <Card>
            <div className="card-body ds-stack">
              <div className="ds-between">
                <h3 style={{ margin: 0 }}>Payment Method</h3>
                <span
                  style={{
                    background: "var(--cf-warning-bg, #fef3c7)",
                    color: "var(--cf-warning-text, #92400e)",
                    padding: "0.125rem 0.5rem",
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Demo
                </span>
              </div>

              <FormField label="Name on card" htmlFor="checkout-card-name">
                <TextInput
                  id="checkout-card-name"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Card number" htmlFor="checkout-card-number">
                <TextInput id="checkout-card-number" value={cardNumber} readOnly />
              </FormField>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <FormField label="Expiration" htmlFor="checkout-card-expiry">
                  <TextInput id="checkout-card-expiry" value={cardExpiry} readOnly />
                </FormField>

                <FormField label="CVC" htmlFor="checkout-card-cvc">
                  <TextInput id="checkout-card-cvc" value={cardCvc} readOnly />
                </FormField>
              </div>
            </div>
          </Card>

          {hasError && (
            <Card>
              <ul>
                {formErrorMessages.map((error, index) => (
                  <li key={index} className="cf-error">
                    {error}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="ds-row">
            <Button type="submit" disabled={checkoutProcessing || saving}>
              {checkoutProcessing ? (
                <>
                  <Spinner size="sm" />
                  Processing demo payment...
                </>
              ) : saving ? (
                <>
                  <Spinner size="sm" />
                  Activating subscription...
                </>
              ) : (
                "Pay $15.00 and subscribe (demo)"
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCheckout(false)}
              disabled={checkoutProcessing || saving}
            >
              Back
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="page ds-stack">
      <PageHeader title={`${bhgCallPlanName} • ${bhgCallPriceLabel}`} sub={bhgCallSummary} />
      <form className="ds-stack" onSubmit={handleProceedToCheckout}>
        <BhgClinicSettings {...clinicSettingsProps} />
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}
        >
          <div className="ds-stack">
            <BhgNotificationScheduleSettings {...scheduleSettingsProps} />
            <BhgNotificationSettings {...notifSettingsProps} />
          </div>
          <div className="ds-stack">
            {bhgIdentityCard}
            <Card>
              <CardHeader
                title="Need help paying?"
                meta="Submit a short application if you need a discount or comp review before checkout."
              />
              <div className="card-body ds-stack">
                {assistanceApplications.length > 0 && (
                  <div className="ds-stack" style={{ gap: "0.5rem" }}>
                    {assistanceApplications.map((application) => (
                      <InlineAlert
                        key={application.id}
                        tone={application.status === "approved" ? "success" : "info"}
                      >
                        <strong>{application.status}</strong> - {application.requestedType} request
                        {application.approvedDiscountPercent
                          ? ` (${application.approvedDiscountPercent}% approved)`
                          : ""}
                        {"."} {application.reason}
                      </InlineAlert>
                    ))}
                  </div>
                )}
                <form className="ds-stack" onSubmit={handleAssistanceSubmit}>
                  <FormField label="Request type" htmlFor="assistance-request-type">
                    <Select
                      id="assistance-request-type"
                      value={assistanceRequestedType}
                      onChange={(event) =>
                        setAssistanceRequestedType(
                          event.target.value === "comp" ? "comp" : "discount"
                        )
                      }
                    >
                      <option value="discount">Discount</option>
                      <option value="comp">Comp</option>
                    </Select>
                  </FormField>
                  {assistanceRequestedType === "discount" && (
                    <FormField label="Requested discount" htmlFor="assistance-requested-percent">
                      <Select
                        id="assistance-requested-percent"
                        value={assistanceDiscountPercent}
                        onChange={(event) => setAssistanceDiscountPercent(event.target.value)}
                      >
                        <option value="10">10%</option>
                        <option value="25">25%</option>
                        <option value="50">50%</option>
                        <option value="75">75%</option>
                      </Select>
                    </FormField>
                  )}
                  <FormField label="Reason" htmlFor="assistance-reason">
                    <TextInput
                      id="assistance-reason"
                      value={assistanceReason}
                      onChange={(event) => setAssistanceReason(event.target.value)}
                      placeholder="Tell us why you need assistance"
                      required
                    />
                  </FormField>
                  <FormField label="Supporting document (optional)" htmlFor="assistance-file">
                    <input
                      id="assistance-file"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(event) => setAssistanceFile(event.target.files?.[0] ?? null)}
                      style={{ fontSize: "0.875rem" }}
                    />
                    <p className="cf-help" style={{ margin: "0.25rem 0 0" }}>
                      PDF, JPG, PNG, or WebP up to 5 MB
                    </p>
                  </FormField>
                  {assistanceMessage && <InlineAlert tone="info">{assistanceMessage}</InlineAlert>}
                  <div className="ds-row" style={{ marginTop: "0.25rem" }}>
                    <Button type="submit" disabled={assistanceSubmitting}>
                      {assistanceSubmitting ? "Submitting..." : "Submit application"}
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        </div>

        {hasError && (
          <Card>
            <ul>
              {formErrorMessages.map((error, index) => (
                <li key={index} className="cf-error">
                  {error}
                </li>
              ))}
            </ul>
          </Card>
        )}
        <div className="ds-row">
          <Button type="submit" disabled={saving}>
            Continue to payment
          </Button>
        </div>
      </form>
    </div>
  );
}
